/**
 * @author Charles Blais <charles.blais@canada.ca>
 */

import moment from 'moment';

/**
 * Networks array
 */
export class Networks extends Array {
  constructor(...args) {
    args = args.map(arg => new Network(arg));
    super(...args);
  }

  get codes() {
    return this.map(network => network.$.code);
  }

  get(code) {
    const element = this.find(network => network.code === code);
    return (element)?element:null;
  }

  /**
   * Convert the information into a single array of stations by simply making
   * an array of stations with the added property of networkCode which is only
   * found in the "Network" code.
   */
  toData() {
    if(!this.length) {
      return this;
    }
    return this.map(network => {
      return network.stations.map(station => {
        station.networkCode = network.code;
        return station;
      });
    }).flat();
  }
}

/**
 * Network object
 */
export class Network {
  constructor(obj) {
    obj && Object.assign(this, obj);
  }

  get code() {
    return this.$.code;
  }

  get stations() {
    return new Stations(...this.Station);
  }
}

/**
 * Stations array
 */
export class Stations extends Array {
  constructor(...args) {
    args = args.map(arg => new Station(arg));
    super(...args);
  }

  get codes() {
    return this.map(station => station.$.code);
  }

  get(code) {
    const element = this.find(station => station.$.code === code);
    return (element)?element:null;
  }
}

/**
 * Station object
 */
export class Station {
  constructor(obj) {
    obj && Object.assign(this, obj);
  }

  get code() {
    return this.$.code;
  }

  get starttime() {
    return (this.$.startDate) ? moment(this.$.startDate) : null;
  }

  get endtime() {
    return (this.$.endDate) ? moment(this.$.endDate) : null;
  }

  get country() {
    return this.Site[0].Country;
  }

  get name() {
    return this.Site[0].Name;
  }

  get latitude() {
    const value = this.Latitude[0];
    return parseFloat((value._) ? value._ : value);
  }

  get longitude() {
    const value = this.Longitude[0];
    return parseFloat((value._) ? value._ : value);
  }

  get elevation() {
    const value = this.Elevation[0];
    return parseFloat((value._) ? value._ : value);
  }

  get channels() {
    return (this.Channel) ? new Channels(...this.Channel) : new Channels();
  }

  /**
   * Determine if the datetime object is within the time range
   * @param {moment} datetime 
   */
  isOpen(datetime) {
    if (this.starttime && this.endtime) {
      return datetime.isBetween(this.startime, this.endtime);
    } else if (this.endtime) {
      return datetime.isBefore(this.endtime);
    } else if (this.starttime) {
      return datetime.isAfter(this.starttime);
    }
    return true;
  }

  /**
   * Determine if the datetime object is before the start time
   * @param {moment} datetime 
   */
  willOpen(datetime) {
    if (this.starttime) {
      return datetime.isBefore(this.starttime);
    }
    return false;
  }

  /**
   * Determine if the datetime object is before the start time
   * @param {moment} datetime 
   */
  isClosed(datetime) {
    if (this.endtime) {
      return datetime.isAfter(this.endtime);
    }
    return false;
  }
}


/**
 * Channels array
 */
export class Channels extends Array {
  constructor(...args) {
    args = args.map(arg => new Channel(arg));
    super(...args);
  }
}


/**
 * Channel object
 */
export class Channel {
  constructor(obj) {
    obj && Object.assign(this, obj);
  }

  get code() {
    return this.$.code;
  }

  get locationCode() {
    return this.$.locationCode;
  }

  get starttime() {
    return (this.$.startDate) ? moment(this.$.startDate) : null;
  }

  get endtime() {
    return (this.$.endDate) ? moment(this.$.endDate) : null;
  }

  get latitude() {
    const value = this.Latitude[0];
    return parseFloat((value._) ? value._ : value);
  }

  get longitude() {
    const value = this.Longitude[0];
    return parseFloat((value._) ? value._ : value);
  }

  get elevation() {
    const value = this.Elevation[0];
    return parseFloat((value._) ? value._ : value);
  }

  get depth() {
    const value = this.Depth[0];
    return parseFloat((value._) ? value._ : value);
  }

  get azimuth() {
    const value = this.Azimuth[0];
    return parseFloat((value._) ? value._ : value);
  }

  get dip() {
    const value = this.Dip[0];
    return parseFloat((value._) ? value._ : value);
  }

  get sampleRate() {
    const value = this.SampleRate[0];
    return parseFloat((value._) ? value._ : value);
  }

  get samplePeriod() {
    return parseFloat(this.SampleRateRatio[0].NumberSeconds)/parseFloat(this.SampleRateRatio[0].NumberSamples);
  }

  get sensors() {
    return (this.Sensor) ? new Sensors(...this.Sensor) : new Sensors();
  }

  /**
   * Determine if the datetime object is within the time range
   * @param {moment} datetime 
   */
  isOpen(datetime) {
    if (this.starttime && this.endtime) {
      return datetime.isBetween(this.startime, this.endtime);
    } else if (this.endtime) {
      return datetime.isBefore(this.endtime);
    } else if (this.starttime) {
      return datetime.isAfter(this.starttime);
    }
    return true;
  }

  /**
   * Determine if the datetime object is before the start time
   * @param {moment} datetime 
   */
   willOpen(datetime) {
    if (this.starttime) {
      return datetime.isBefore(this.starttime);
    }
    return false;
  }

  /**
   * Determine if the datetime object is before the start time
   * @param {moment} datetime 
   */
  isClosed(datetime) {
    if (this.endtime) {
      return datetime.isAfter(this.endtime);
    }
    return false;
  }
}


/**
 * Channels array
 */
export class Sensors extends Array {
  constructor(...args) {
    args = args.map(arg => new Sensor(arg));
    super(...args);
  }
}


/**
 * Sensor object
 */
export class Sensor {
  constructor(obj) {
    obj && Object.assign(this, obj);
  }

  get description() {
    return this.Description;
  }

}

export default Networks;